/**
 * The procedurally generated universe.
 *
 * None of this is stored. Every value here is derived from
 * (worldSeed, generatorVersion, entity id), so the whole universe is a pure
 * function that costs nothing to keep and cannot drift between servers.
 *
 * Positions are 2D: flight happens on a plane, matching the 2D physics. The
 * third dimension exists only in the renderer.
 */
/**
 * Landing pads and the ships that fit them. A ship can use a pad of its own
 * size class or larger, never smaller — an outpost with only small pads simply
 * cannot take a freighter.
 */
export const SIZE_CLASSES = ['small', 'medium', 'large', 'massive'];
/**
 * Single-letter pad markings. "massive" is X rather than M, which medium
 * already has.
 */
export const PAD_LABELS = {
    small: 'S',
    medium: 'M',
    large: 'L',
    massive: 'X',
};
export function sizeRank(size) {
    return SIZE_CLASSES.indexOf(size);
}
/** Whether a ship of `shipSize` may dock at a pad of `padSize`. */
export function padFitsShip(padSize, shipSize) {
    return sizeRank(padSize) >= sizeRank(shipSize);
}
/** Small 1, medium 2, large 3, massive 4 — what a pad contributes to station size. */
export function padScore(size) {
    return sizeRank(size) + 1;
}
/**
 * A station's effective size, summed across its pads.
 *
 * Anything at or above `SUN_ORBIT_SCORE` is too large to hang off a planet and
 * orbits the star directly.
 */
export function stationSizeScore(pads) {
    return pads.reduce((total, pad) => total + padScore(pad.size), 0);
}
export const SUN_ORBIT_SCORE = 20;
/**
 * Pad side length in world units, from the design doc. A pad is square, and a
 * ship fits if its widest extent is under this.
 */
export const PAD_SIZES = {
    small: 25,
    medium: 50,
    large: 70,
    massive: 100,
};
/**
 * Surfaces a moon can have.
 *
 * Airless bodies only. A moon with oceans or a jungle would be a planet that
 * happens to orbit a planet, and nothing in the game treats it that way.
 */
export const MOON_TYPES = ['rocky', 'barren', 'ice', 'volcanic'];
/**
 * What a planet looks like, which selects its shader.
 *
 * Type governs *pattern*, never colour: an ocean world is defined by having
 * seas and coastlines, not by being blue. A violet ocean or an orange jungle is
 * intended — the palette is rolled independently.
 */
export const PLANET_TYPES = [
    'ocean',
    'islandic',
    'jungle',
    'desert',
    'rocky',
    'barren',
    'ice',
    'volcanic',
    'toxic',
    'gasGiant',
];
/** Below this radius a world is too small to hold onto a thick atmosphere. */
export const GAS_GIANT_MIN_RADIUS = 95;
/** Planets below this radius never have moons. */
export const MOON_MIN_PLANET_RADIUS = 75;
/** Closest the orbit comes to the star. */
export function periapsis(orbit) {
    return orbit.semiMajorAxis * (1 - orbit.eccentricity);
}
/** Furthest the orbit takes the planet from the star. */
export function apoapsis(orbit) {
    return orbit.semiMajorAxis * (1 + orbit.eccentricity);
}
/**
 * Where a planet sits, in system-local 3D coordinates with the star at origin.
 *
 * Standard perifocal-to-inertial transform: find the radius for the current
 * true anomaly, place the point in the orbital plane, then rotate that plane by
 * argument of periapsis, inclination, and ascending node in that order.
 */
export function planetPosition(orbit) {
    const { semiMajorAxis: a, eccentricity: e, trueAnomaly: v } = orbit;
    const r = (a * (1 - e * e)) / (1 + e * Math.cos(v));
    // In the orbital plane, measured from periapsis.
    const xp = r * Math.cos(v);
    const yp = r * Math.sin(v);
    const cosW = Math.cos(orbit.argumentOfPeriapsis);
    const sinW = Math.sin(orbit.argumentOfPeriapsis);
    const cosI = Math.cos(orbit.inclination);
    const sinI = Math.sin(orbit.inclination);
    const cosO = Math.cos(orbit.ascendingNode);
    const sinO = Math.sin(orbit.ascendingNode);
    // Rotate within the plane first, then tilt, then swing the plane around.
    const x1 = xp * cosW - yp * sinW;
    const y1 = xp * sinW + yp * cosW;
    const y2 = y1 * cosI;
    const z2 = y1 * sinI;
    return {
        x: x1 * cosO - y2 * sinO,
        y: x1 * sinO + y2 * cosO,
        z: z2,
    };
}
/** Points along a full orbit, for drawing the path. */
export function orbitPath(orbit, segments = 128) {
    return Array.from({ length: segments }, (_, i) => planetPosition({ ...orbit, trueAnomaly: (i / segments) * Math.PI * 2 }));
}
/**
 * Seconds for one revolution.
 *
 * Kepler's third law would use an exponent of 1.5, which is correct and looks
 * terrible: the outermost planet would take thirty times longer than the
 * innermost, so on any watchable timescale it simply sits still. The exponent
 * here is flattened for legibility — outer orbits are still visibly slower,
 * just not uselessly so. This is a map, not an ephemeris.
 */
const PERIOD_EXPONENT = 0.9;
const PERIOD_SCALE = 0.2424;
export function orbitalPeriod(orbit) {
    return PERIOD_SCALE * Math.pow(orbit.semiMajorAxis, PERIOD_EXPONENT);
}
/**
 * The same orbit advanced to a moment in time.
 *
 * Advances true anomaly directly rather than solving Kepler's equation. With
 * eccentricity capped low, the difference between that and a correct solution
 * is far below a pixel, and it avoids an iterative solve per body per frame.
 */
export function orbitAt(orbit, seconds) {
    const advance = (seconds / orbitalPeriod(orbit)) * Math.PI * 2;
    return { ...orbit, trueAnomaly: orbit.trueAnomaly + advance };
}
/** Where a body sits at a given time, relative to whatever it orbits. */
export function positionAt(orbit, seconds) {
    return planetPosition(orbitAt(orbit, seconds));
}
//# sourceMappingURL=types.js.map